import { useRef } from "react";
import { ItemArt } from "../art/ItemArt";
import { itemById, itemsOfKind, type ItemKind } from "../content/items";
import type { Ninja } from "../content/ninjas";
import { levelHeld, loadoutFor, type Loadouts, type OwnedItems } from "../game/items";
import { Modal } from "./Modal";
import "./ItemPicker.css";

interface Props {
  /** Which slot is being filled, or null when closed. */
  slot: { ninja: Ninja; kind: ItemKind; index: number } | null;
  loadouts: Loadouts;
  items: OwnedItems;
  onChoose: (id: string | null) => void;
  onClose: () => void;
  /** Go to the Dojo Shop, for a child with nothing to fit. */
  onShop?: (() => void) | undefined;
}

/** A sheet of the owned items of a slot's kind, with "Take off" for a filled slot. */
export function ItemPicker({ slot, loadouts, items, onChoose, onClose, onShop }: Props) {
  const closeBtn = useRef<HTMLButtonElement>(null);
  if (!slot) return null;
  const { ninja, kind, index } = slot;
  const owned = itemsOfKind(kind).filter((it) => levelHeld(items, it.id) > 0);
  const current = loadoutFor(loadouts, ninja)[kind][index] ?? null;
  const currentItem = current ? itemById(current) : undefined;
  const title = kind === "active" ? "Use in a fight" : "Wear";
  return (
    <Modal open label={`Fit an item to ${ninja.name}`} onClose={onClose} initialFocus={closeBtn}>
      <div className="panel picker">
        <h2 className="display picker-title">
          {title}: {ninja.name}
        </h2>
        {owned.length === 0 ? (
          <p className="dmotto">Nothing to fit yet. Visit the Dojo Shop.</p>
        ) : (
          <ul className="picker-list">
            {owned.map((it) => {
              const level = levelHeld(items, it.id);
              const elsewhere = loadoutFor(loadouts, ninja)[kind].indexOf(it.id);
              const here = it.id === current;
              return (
                <li key={it.id}>
                  <button
                    type="button"
                    className={"picker-item" + (here ? " here" : "")}
                    aria-pressed={here}
                    onClick={() => onChoose(it.id)}
                  >
                    <ItemArt id={it.id} className="picker-art" />
                    <span className="picker-text">
                      <span className="display picker-name">
                        {it.name}
                        {it.levels.length > 1 ? ` · level ${level}` : ""}
                      </span>
                      <span className="picker-what">{it.levels[level - 1]!.text}</span>
                    </span>
                    {here ? (
                      <span className="picker-tag">Fitted</span>
                    ) : elsewhere >= 0 ? (
                      <span className="picker-tag soft">Slot {elsewhere + 1}</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <div className="picker-row">
          {currentItem && (
            <button type="button" className="mini" onClick={() => onChoose(null)}>
              Take off {currentItem.name}
            </button>
          )}
          {owned.length === 0 && onShop && (
            <button type="button" className="mini" onClick={onShop}>
              Dojo Shop
            </button>
          )}
          <button
            ref={closeBtn}
            type="button"
            className="big display picker-close"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}
